import { createContext, useContext } from 'react'

/**
 * The toast API, in its own module.
 *
 * Split from `ToastProvider.tsx` because `react-refresh/only-export-components`
 * — a warning, and `eslint --max-warnings=0` makes warnings fatal — refuses a
 * module that exports both a component and a hook. The same split appears in
 * `auth/` and `tenant/` for the same reason.
 */

export type ToastKind = 'success' | 'warning' | 'danger' | 'info'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  body?: string
}

export interface ToastApi {
  /** Show one. Returns its id, so a caller can dismiss it early. */
  show: (toast: Omit<Toast, 'id'>) => number
  dismiss: (id: number) => void
}

export const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const value = useContext(ToastContext)
  if (!value) {
    // A hard failure rather than a no-op fallback: a toast that silently does
    // nothing is how "the save button does nothing" bugs are born, and the
    // cause — a provider missing from the tree — is a wiring mistake that
    // should surface the first time it is rendered, not in production.
    throw new Error('useToast was called outside <ToastProvider>')
  }
  return value
}
